// @flow
import * as React from 'react';
import {
    CollapsibleFixedDataTable,
    collapsibleTableFactory,
    SelectableFixedDataTable
} from 'common/components/core/table';
import { themr } from 'react-css-themr';
import { translate } from 'react-i18next';
import theme from './EntityIdValidationTable.scss';
import { EntityImportTable } from 'common/models/internal/EntityImportTable';
import Loader from 'common/components/core/loader/Loader';
import type { EntityImportHeaderModelType } from 'common/models/flow/EntityImportTable';
import type { FixedDataTableGetCellPropsTypes } from 'common/components/core/table/FixedDataTable';

const CollapsibleSelectableFixedDataTable = collapsibleTableFactory(
    SelectableFixedDataTable
);

type Props = {
    theme: Object,
    existingEntitiesTable: ?EntityImportTable,
    newEntitiesTable: ?EntityImportTable,
    verifiedRowIds: Array<string>,
    ignoredRowIds: Array<string>,
    isValidatingIds: boolean,
    onDuplicatesSelectionChange: Function,
    showNonImportableColumns: boolean
};
type State = {
    isExistingEntitiesTableExpanded: boolean,
    isNewEntitiesTableExpanded: boolean,
    isReservedEntitiesTableExpanded: boolean
};

class EntityIdValidationTable extends React.PureComponent<Props, State> {
    state = {
        isExistingEntitiesTableExpanded: true,
        isNewEntitiesTableExpanded: false,
        isReservedEntitiesTableExpanded: false
    };

    handleDuplicatesCardClick = () => {
        this.setState(prevState => ({
            isExistingEntitiesTableExpanded: !prevState.isExistingEntitiesTableExpanded
        }));
    };

    handleValidatedCardClick = () => {
        this.setState(prevState => ({
            isNewEntitiesTableExpanded: !prevState.isNewEntitiesTableExpanded
        }));
    };

    handleReservedCardClick = () => {
        this.setState(prevState => ({
            isReservedEntitiesTableExpanded: !prevState.isReservedEntitiesTableExpanded
        }));
    };

    renderColumn = ({ header }: { header: EntityImportHeaderModelType }) => {
        const { showNonImportableColumns } = this.props;
        if (!showNonImportableColumns && !header.importable) {
            return null; // hide non importable columns
        }
        return;
    };

    getCellProps = ({
        header
    }: FixedDataTableGetCellPropsTypes): {
        cellWrapperClassName: ?string
    } => ({
        cellWrapperClassName: !header.importable ? theme.darkBG : null
    });

    render() {
        const {
            existingEntitiesTable,
            newEntitiesTable,
            reservedEntitiesTable,
            onDuplicatesSelectionChange,
            theme,
            verifiedRowIds,
            ignoredRowIds,
            t,
            isValidatingIds,
            entityNameId,
            showNonImportableColumns,
            conflictEntities = t('entityImport.otherEntity')
        } = this.props;
        const {
            isExistingEntitiesTableExpanded,
            isNewEntitiesTableExpanded,
            isReservedEntitiesTableExpanded
        } = this.state;
        let existingEntitiesCount = 0;
        let newEntitiesCount = 0;
        let reservedEntitiesCount = 0;
        if (existingEntitiesTable && newEntitiesTable) {
            existingEntitiesCount = existingEntitiesTable.rows.size;
            newEntitiesCount = newEntitiesTable.rows.size;
        }
        if (reservedEntitiesTable) {
            reservedEntitiesCount = reservedEntitiesTable.rows.size;
        }
        const totalEntitiesCount =
            existingEntitiesCount + newEntitiesCount + reservedEntitiesCount;
        const commonProps = {
            /*
                showNonImportableColumns not using internally
                but want to re-render fixedDataTable when this changes
                recompose blocks updates until props are not changed
            */
            showNonImportableColumns,
            renderColumn: this.renderColumn,
            getCellProps: this.getCellProps
        };
        if (isValidatingIds) {
            return (
                <Loader
                    active
                    theme={theme}
                    withText
                    title={t('entityImport.validatingIds')}
                />
            );
        }
        return (
            <div className={theme.tables}>
                {existingEntitiesTable?.rows.size ? (
                    <CollapsibleSelectableFixedDataTable
                        onClick={this.handleDuplicatesCardClick}
                        isExpanded={isExistingEntitiesTableExpanded}
                        title={t('entityImport.existingEntitiesTitle', {
                            count: existingEntitiesCount,
                            entityNameId
                        })}
                        subtitleWarning={t(
                            'entityImport.existingEntitiesWarning',
                            {
                                totalEntitiesCount,
                                existingEntitiesCount
                            }
                        )}
                        tableData={existingEntitiesTable}
                        theme={theme}
                        verifiedRowIds={verifiedRowIds}
                        ignoredRowIds={ignoredRowIds}
                        onTableSelectionChange={onDuplicatesSelectionChange}
                        {...commonProps}
                    />
                ) : null}
                {reservedEntitiesTable?.rows.size ? (
                    <CollapsibleFixedDataTable
                        onClick={this.handleReservedCardClick}
                        isExpanded={isReservedEntitiesTableExpanded}
                        title={t('entityImport.reservedEntitiesTitle', {
                            count: reservedEntitiesTable.rows.size,
                            entityNameId
                        })}
                        subtitleWarning={t(
                            'entityImport.reservedEntitiesWarning',
                            {
                                totalEntitiesCount,
                                reservedEntitiesCount,
                                conflictEntities
                            }
                        )}
                        tableData={reservedEntitiesTable}
                        theme={theme}
                        {...commonProps}
                    />
                ) : null}
                {newEntitiesTable?.rows.size ? (
                    <CollapsibleFixedDataTable
                        onClick={this.handleValidatedCardClick}
                        isExpanded={isNewEntitiesTableExpanded}
                        title={t('entityImport.newEntitiesTitle', {
                            count: newEntitiesCount,
                            entityNameId
                        })}
                        tableData={newEntitiesTable}
                        theme={theme}
                        {...commonProps}
                    />
                ) : null}
            </div>
        );
    }
}
export default translate('entityManagement')(
    themr('EntityIdValidationTable', theme)(EntityIdValidationTable)
);
